/**
 * Step 3, before the plans run — the questions that change the answer, inline (version A, owner
 * decision 17 Oct 2026, retiring the 22 Sep "keep the setup pop-over" ruling).
 *
 * The same five questions "Things that change the answer" reopens under the plans (EngineQuestions),
 * drawn where the plans will appear, pre-filled with the suggested answer and the reason. The
 * answers land in the analyser's setup bag (coupleValidation), the annuity answer on the client's
 * goals, and the horizon in the analyser — the engine inputs are unchanged.
 */
import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import EngineQuestions, { answersFromSetup, setupFromAnswers, annuityGoalsFromAnswers, type EngineAnswers } from './EngineQuestions.tsx';
import type { CoupleValidationState } from './AgedCareAnalyser.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { BuildYourselfLine } from '../../components/strategy/QuestionParts.tsx';

interface EngineSetupInlineProps {
    factFind: DraftFactFind;
    setFactFind: (updater: DraftFactFind | ((prev: DraftFactFind) => DraftFactFind)) => void;
    coupleValidation: CoupleValidationState;
    setCoupleValidation: (value: CoupleValidationState) => void;
    scenarioLivingExpenses: number;
    defaultPartnerRoomPrice: number;
    legacyYears: number;
    onLegacyYears: (years: number) => void;
    enteringName: string;
    onDone: () => void;
    onBuildYourOwn?: (row: null) => void;
    /** The room's full deposit, when known (the guaranteed income share is of what it leaves). */
    roomDeposit?: number;
}

const EngineSetupInline = ({ factFind, setFactFind, coupleValidation, setCoupleValidation, scenarioLivingExpenses, defaultPartnerRoomPrice, roomDeposit, legacyYears, onLegacyYears, enteringName, onDone, onBuildYourOwn }: EngineSetupInlineProps) => {
    const [answers, setAnswers] = useState<EngineAnswers>(() => answersFromSetup(coupleValidation as never, factFind, legacyYears, scenarioLivingExpenses, defaultPartnerRoomPrice));
    // "You" for the client, "John and Kerry" for an adviser (householdVoice, 21 Oct 2026).
    const household = householdVoice(factFind).names;
    const questionCount = 2 + ((Number(factFind.home?.value) || 0) > 0 ? 1 : 0) + (factFind.adviceFor === 'couple' && (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for !== 'both' ? 1 : 0) + 1 + ((factFind.structures || []).length > 0 ? 1 : 0);
    const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][questionCount] || String(questionCount);

    // Only a guaranteed income answer given here is written: the client's earlier answer, from any module, is
    // never overwritten by a default (guaranteed income review, 21 Oct 2026).
    const writeAnnuityGoals = () => {
        const annuityGoals = annuityGoalsFromAnswers(answers, factFind);
        if (annuityGoals) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), ...annuityGoals } }));
    };
    const submit = () => {
        setCoupleValidation(setupFromAnswers(coupleValidation as never, answers) as unknown as CoupleValidationState);
        setFactFind((prev) => ({
            ...prev,
            ...((prev.structures || []).length > 0 && answers.structureFunding ? { structureFundingInSuggestions: answers.structureFunding } : {}),
        }));
        writeAnnuityGoals();
        onLegacyYears(answers.years);
        onDone();
    };

    return (
        <>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="engine-setup-inline">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Before we test the ways to pay</p>
                <h2 className="mt-1 text-[22px] font-bold leading-snug tracking-tight text-slate-900">
                    {words} things change the answer. We&apos;ve filled in what we&apos;d suggest — change any of them, or just go on.
                </h2>
                {onBuildYourOwn && <BuildYourselfLine onClick={() => { writeAnnuityGoals(); onBuildYourOwn(null); }} testId="engine-setup-build-own-top" />}
                <div className="mt-6">
                    <EngineQuestions value={answers} onChange={setAnswers} factFind={factFind} scenarioLivingExpenses={scenarioLivingExpenses} idPrefix="setup" roomDeposit={roomDeposit} />
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-5">
                    <p className="text-[13px] text-slate-600">We&apos;ll test a few hundred ways to pay for the room and show the ones that best fit what matters to {household}.</p>
                    {/* The two ways on, side by side (owner, 20 Oct 2026: the build-your-own link sat in small
                        print under the form and "effectively removed my ability to model my own plan"). */}
                    <div className="flex flex-wrap items-center gap-3">
                        {onBuildYourOwn && (
                            <button type="button" onClick={() => { writeAnnuityGoals(); onBuildYourOwn(null); }}className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-[15px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid="engine-setup-build-own">
                                Build a plan yourself instead
                            </button>
                        )}
                        <button type="button" onClick={submit} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 text-[15px] font-semibold text-white shadow-md " data-testid="engine-setup-continue">
                            Show me the plans <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EngineSetupInline;
